export type VideoSourceType = 'youtube' | 'vimeo' | 'direct' | 'unknown';

const DIRECT_VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov)(\?.*)?$/i;

const YOUTUBE_PATTERNS = [
  // https://www.youtube.com/watch?v=ID
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  // https://www.youtube.com/embed/ID
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  // https://youtu.be/ID
  /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
  // https://www.youtube.com/shorts/ID
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
];

const VIMEO_PATTERNS = [
  // https://vimeo.com/123456789
  /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
  // https://player.vimeo.com/video/123456789
  /(?:https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/,
];

export function extractYouTubeId(url: string): string | null {
  for (const pattern of YOUTUBE_PATTERNS) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function extractVimeoId(url: string): string | null {
  for (const pattern of VIMEO_PATTERNS) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function isDirectVideoUrl(url: string): boolean {
  return DIRECT_VIDEO_EXTENSIONS.test(url);
}

export function detectVideoSourceType(url: string): VideoSourceType {
  if (!url) return 'unknown';
  if (isDirectVideoUrl(url)) return 'direct';
  if (extractYouTubeId(url)) return 'youtube';
  if (extractVimeoId(url)) return 'vimeo';
  return 'unknown';
}

export function toEmbedUrl(url: string): string {
  const ytId = extractYouTubeId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}`;

  const vimeoId = extractVimeoId(url);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;

  return url;
}

export function validateVideoUrl(url: string): string | null {
  if (!url) return null;
  try {
    new URL(url);
  } catch {
    return 'Please enter a valid URL.';
  }
  const type = detectVideoSourceType(url);
  if (type === 'unknown') {
    return 'Unrecognized video URL. Supported: YouTube, Vimeo, or direct video files (.mp4, .webm, .ogg, .mov).';
  }
  return null;
}
